import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useContextoSesion } from '../miscelaneo/ContextoSesion';
import qzSerialService from '../services/qzSerialService';
import { parsearPeso } from '../services/protocolosBalanza';

const ContextoBalanza = createContext();

export const useBalanza = () => useContext(ContextoBalanza);

export const ContextoBalanzaProvider = ({ children }) => {
  const { sesionCaja } = useContextoSesion();

  const [pesoActual, setPesoActual] = useState(0);
  const [unidadPeso, setUnidadPeso] = useState('kg');
  const [balanzaEstable, setBalanzaEstable] = useState(false);
  const [balanzaConectada, setBalanzaConectada] = useState(false);
  const [conectando, setConectando] = useState(false);

  // Locking: peso capturado que no cambia con fluctuaciones
  const [pesoBloqueado, setPesoBloqueado] = useState(null);
  // Timeout: sin datos de la balanza por >5s
  const [sinDatos, setSinDatos] = useState(false);

  const puertoRef = useRef(null);
  const protocoloRef = useRef('zebra');
  const ultimaLecturaRef = useRef(null);
  const reconectandoRef = useRef(false);
  const intentosReconexionRef = useRef(0);

  const conexionHabilitada = sesionCaja?.conexion_balanza === 'serial' && !!sesionCaja?.puerto_balanza;

  const conectarBalanza = useCallback(async () => {
    if (!conexionHabilitada || balanzaConectada || conectando) return false;

    const puerto = sesionCaja.puerto_balanza;
    const baudRate = sesionCaja.baudrate_balanza || 9600;
    const protocolo = sesionCaja.protocolo_balanza || 'zebra';

    puertoRef.current = puerto;
    protocoloRef.current = protocolo;
    setConectando(true);

    try {
      qzSerialService.setCallbackSerial((evt) => {
        if (evt?.type === 'ERROR') {
          console.error('[Balanza] Error serial:', evt.exception);
          return;
        }
        if (!evt?.output) return;
        const resultado = parsearPeso(protocoloRef.current, evt.output);
        if (resultado) {
          setPesoActual(resultado.peso);
          setUnidadPeso(resultado.unidad);
          setBalanzaEstable(resultado.estable);
          ultimaLecturaRef.current = Date.now();
          setSinDatos(false);
        }
      });

      const ok = await qzSerialService.conectarPuerto(puerto, { baudRate, protocolo });
      setBalanzaConectada(ok);
      if (ok) {
        await qzSerialService.enviarComandoProtocolo(puerto, protocolo);
        ultimaLecturaRef.current = Date.now();
        setSinDatos(false);
        intentosReconexionRef.current = 0;
      } else {
        console.warn('[Balanza] No se pudo conectar al puerto', puerto);
      }
      return ok;
    } catch (err) {
      console.error('[Balanza] Error al conectar:', err);
      setBalanzaConectada(false);
      return false;
    } finally {
      setConectando(false);
    }
  }, [conexionHabilitada, balanzaConectada, conectando, sesionCaja]);

  const desconectarBalanza = useCallback(async () => {
    if (!puertoRef.current) return;
    try {
      await qzSerialService.desconectarPuerto(puertoRef.current);
    } catch (err) {
      console.error('[Balanza] Error al desconectar:', err);
    }
    setBalanzaConectada(false);
    setPesoActual(0);
    setBalanzaEstable(false);
    setSinDatos(false);
    ultimaLecturaRef.current = null;
    puertoRef.current = null;
  }, []);

  // Bloquear/desbloquear peso
  const bloquearPeso = useCallback(() => {
    if (pesoActual > 0) {
      setPesoBloqueado({ peso: pesoActual, unidad: unidadPeso, timestamp: Date.now() });
    }
  }, [pesoActual, unidadPeso]);

  const desbloquearPeso = useCallback(() => setPesoBloqueado(null), []);

  // Reconexion automatica
  const reconectarBalanza = useCallback(async () => {
    if (reconectandoRef.current || !conexionHabilitada) return;
    if (intentosReconexionRef.current >= 3) {
      console.warn('[Balanza] Max intentos de reconexion alcanzados');
      return;
    }

    reconectandoRef.current = true;
    intentosReconexionRef.current += 1;
    const intento = intentosReconexionRef.current;
    const backoff = Math.pow(2, intento) * 1000; // 2s, 4s, 8s

    console.log(`[Balanza] Reconexion intento ${intento}/3 (backoff ${backoff}ms)`);

    try {
      // Desconectar primero
      if (puertoRef.current) {
        try {
          await qzSerialService.desconectarPuerto(puertoRef.current);
        } catch {}
        setBalanzaConectada(false);
        puertoRef.current = null;
      }

      await new Promise(r => setTimeout(r, backoff));

      // Reconectar
      const puerto = sesionCaja.puerto_balanza;
      const baudRate = sesionCaja.baudrate_balanza || 9600;

      puertoRef.current = puerto;
      setConectando(true);

      qzSerialService.setCallbackSerial((evt) => {
        if (evt?.type === 'ERROR') {
          console.error('[Balanza] Error serial (reconexion):', evt.exception);
          return;
        }
        if (!evt?.output) return;
        const resultado = parsearPeso(protocoloRef.current, evt.output);
        if (resultado) {
          setPesoActual(resultado.peso);
          setUnidadPeso(resultado.unidad);
          setBalanzaEstable(resultado.estable);
          ultimaLecturaRef.current = Date.now();
          setSinDatos(false);
        }
      });

      const protocolo = sesionCaja.protocolo_balanza || 'zebra';
      const ok = await qzSerialService.conectarPuerto(puerto, { baudRate, protocolo });
      setBalanzaConectada(ok);
      if (ok) {
        await qzSerialService.enviarComandoProtocolo(puerto, protocolo);
        ultimaLecturaRef.current = Date.now();
        setSinDatos(false);
        intentosReconexionRef.current = 0;
        console.log('[Balanza] Reconexion exitosa');
      }
    } catch (err) {
      console.error('[Balanza] Error en reconexion:', err);
    } finally {
      setConectando(false);
      reconectandoRef.current = false;
    }
  }, [conexionHabilitada, sesionCaja]);

  // Auto-connect when session has scale config
  useEffect(() => {
    if (conexionHabilitada && !balanzaConectada && !conectando) {
      const timer = setTimeout(() => conectarBalanza(), 2000);
      return () => clearTimeout(timer);
    }
    if (!conexionHabilitada && balanzaConectada) {
      desconectarBalanza();
    }
  }, [conexionHabilitada, balanzaConectada, conectando, conectarBalanza, desconectarBalanza]);

  // Deteccion de timeout: si no hay datos en 5s → sinDatos = true → intentar reconexion
  useEffect(() => {
    if (!balanzaConectada) return;
    const intervalo = setInterval(() => {
      if (ultimaLecturaRef.current && Date.now() - ultimaLecturaRef.current > 5000) {
        setSinDatos(true);
      }
    }, 2000);
    return () => clearInterval(intervalo);
  }, [balanzaConectada]);

  // Reconexion automatica cuando se detecta timeout
  useEffect(() => {
    if (sinDatos && balanzaConectada && !reconectandoRef.current) {
      reconectarBalanza();
    }
  }, [sinDatos, balanzaConectada, reconectarBalanza]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (puertoRef.current) {
        qzSerialService.desconectarPuerto(puertoRef.current).catch(() => {});
      }
    };
  }, []);

  const value = {
    pesoActual,
    unidadPeso,
    balanzaEstable,
    balanzaConectada,
    conectando,
    conexionHabilitada,
    conectarBalanza,
    desconectarBalanza,
    // Locking
    pesoBloqueado,
    bloquearPeso,
    desbloquearPeso,
    // Timeout / reconexion
    sinDatos,
    reconectarBalanza,
  };

  return (
    <ContextoBalanza.Provider value={value}>
      {children}
    </ContextoBalanza.Provider>
  );
};

export default ContextoBalanza;
